import { api } from "@/redux/api";

import {
	AiModel,
	AiModelSchema,
	AiModelUseCase,
	CompanyCosts,
	CompanyCostsSchema,
	CourseFileInfo,
	CourseFileInfoSchema,
	LibraryAsset,
	LibraryAssetSchema,
	MediaFolderSummary,
	MediaFolderSummarySchema,
	MediaFolderTree,
	MediaFolderTreeSchema,
	EstimateQuantities,
	EstimateSchema,
	FormatPreset,
	FormatPresetSchema,
	MediaEstimate,
	MediaJob,
	MediaJobEnvelopeSchema,
	MediaJobListSchema,
	MediaJobSchema,
	MediaJobStatus,
	MediaKind,
	MusicTrack,
	MusicTrackSchema,
	QualityTier,
	ScenarioCosts,
	ScenarioCostsSchema,
} from "./media.schema";

export interface CreatePhaseImageJobPayload {
	courseId: number;
	phaseDataId?: number;
	prompt: string;
	qualityTier?: QualityTier;
	aiModelId?: number;
	formatPresetIds: number[];
	variantCount?: number;
}

/**
 * What narrows the library. `folderId` 0 is the root — the files nobody has filed; omitted,
 * every file wherever it is. All of it composes: the folder, the scenario, the kind and
 * the sort apply together.
 */
export interface MediaLibraryQuery {
	courseId?: number;
	kind?: "video" | "image" | "audio" | "other";
	sortBy?: "date" | "name";
	sortDir?: "asc" | "desc";
	folderId?: number;
	includeDescendants?: boolean;
}

export interface MediaJobQuery {
	courseId?: number;
	status?: MediaJobStatus;
	page?: number;
	limit?: number;
}

export interface CreateFormatPresetPayload {
	key: string;
	label: string;
	width: number;
	height: number;
	aspectRatio: string;
	mediaKind?: MediaKind;
	scope?: FormatPreset["scope"];
	sortOrder?: number;
}

export type UpdateFormatPresetPayload = Partial<
	Omit<CreateFormatPresetPayload, "key" | "scope">
> & {
	enabled?: boolean;
	hiddenForCompanyIds?: number[];
};

export interface CompanyCostsQuery {
	/** ISO dates. Both optional — the server defaults to the last twelve calendar months. */
	from?: string;
	to?: string;
}

export interface CreateMusicTrackPayload {
	name: string;
	licence: string;
	description?: string;
	durationSeconds?: number;
	sortOrder?: number;
	file: File;
}

export type UpdateMusicTrackPayload = Partial<
	Omit<CreateMusicTrackPayload, "file" | "durationSeconds">
> & {
	enabled?: boolean;
};

export interface GenerateMusicTrackPayload {
	prompt: string;
	name: string;
	description?: string;
	durationSeconds?: number;
	licence?: string;
}

export interface UploadToLibraryPayload {
	courseId: number;
	file: File;
}

export interface AssignAssetPayload {
	kind: "generated" | "uploaded";
	assetId: number;
	/** The scenario to put it in — not necessarily the one that generated it. */
	courseId: number;
	fileAssignment: string;
}

export interface UpdateAiModelPayload {
	displayName?: string;
	unitPriceUsd?: number;
	defaultVariantCount?: number;
	pricesVerified?: boolean;
	isDefault?: boolean;
	enabled?: boolean;
	sortOrder?: number;
}

export const mediaApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getFormatPresets: builder.query<FormatPreset[], { mediaKind?: MediaKind } | void>({
			query: (params) => ({
				url: "media/format-presets",
				params: params?.mediaKind ? { mediaKind: params.mediaKind } : undefined,
				responseSchema: FormatPresetSchema.array(),
			}),
			providesTags: ["FormatPresets"],
		}),
		createFormatPreset: builder.mutation<FormatPreset, CreateFormatPresetPayload>({
			query: (body) => ({
				url: "media/format-presets",
				method: "POST",
				body,
				responseSchema: FormatPresetSchema,
			}),
			invalidatesTags: ["FormatPresets"],
		}),
		updateFormatPreset: builder.mutation<FormatPreset, { id: number } & UpdateFormatPresetPayload>({
			query: ({ id, ...body }) => ({
				url: `media/format-presets/${id}`,
				method: "PATCH",
				body,
				responseSchema: FormatPresetSchema,
			}),
			invalidatesTags: ["FormatPresets"],
		}),
		deleteFormatPreset: builder.mutation<void, number>({
			query: (id) => ({
				url: `media/format-presets/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["FormatPresets"],
		}),

		getAiModels: builder.query<AiModel[], { useCase?: AiModelUseCase } | void>({
			query: (params) => ({
				url: "media/models",
				params: params?.useCase ? { useCase: params.useCase } : undefined,
				responseSchema: AiModelSchema.array(),
			}),
			providesTags: ["AiModels"],
		}),
		updateAiModel: builder.mutation<AiModel, { id: number } & UpdateAiModelPayload>({
			query: ({ id, ...body }) => ({
				url: `media/models/${id}`,
				method: "PATCH",
				body,
				responseSchema: AiModelSchema,
			}),
			invalidatesTags: ["AiModels"],
		}),

		/**
		 * The library. `includeDisabled` is honoured for a platform admin and ignored for
		 * anyone else, so the wizard's picker and the admin table can share one endpoint.
		 */
		getMusicTracks: builder.query<MusicTrack[], { includeDisabled?: boolean } | void>({
			query: (params) => ({
				url: "media/music-tracks",
				params: params?.includeDisabled ? { includeDisabled: true } : undefined,
				responseSchema: MusicTrackSchema.array(),
			}),
			providesTags: ["MusicTracks"],
		}),
		/** Multipart: the file and its metadata arrive together, as an upload does elsewhere. */
		createMusicTrack: builder.mutation<MusicTrack, CreateMusicTrackPayload>({
			query: ({ file, ...fields }) => {
				const body = new FormData();

				body.append("file", file);
				Object.entries(fields).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== "") {
						body.append(key, String(value));
					}
				});

				return {
					url: "media/music-tracks",
					method: "POST",
					body,
					responseSchema: MusicTrackSchema,
				};
			},
			invalidatesTags: ["MusicTracks"],
		}),
		updateMusicTrack: builder.mutation<MusicTrack, { id: number } & UpdateMusicTrackPayload>({
			query: ({ id, ...body }) => ({
				url: `media/music-tracks/${id}`,
				method: "PATCH",
				body,
				responseSchema: MusicTrackSchema,
			}),
			invalidatesTags: ["MusicTracks"],
		}),
		deleteMusicTrack: builder.mutation<void, number>({
			query: (id) => ({
				url: `media/music-tracks/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["MusicTracks"],
		}),
		/**
		 * 202 with the job. The library row appears when the audio lands, so the table is
		 * refetched off the socket rather than from this response.
		 */
		generateMusicTrack: builder.mutation<MediaJob, GenerateMusicTrackPayload>({
			query: (body) => ({
				url: "media/music-tracks/generate",
				method: "POST",
				body,
				responseSchema: MediaJobEnvelopeSchema,
			}),
			transformResponse: (response: { job: MediaJob }) => response.job,
			invalidatesTags: ["MediaJobs", "MediaCosts", "MusicTracks"],
		}),

		/**
		 * One job renders every selected format, so its cost is variants × formats. The
		 * 202 lands before any image exists — progress arrives over the socket.
		 */
		createPhaseImageJob: builder.mutation<MediaJob, CreatePhaseImageJobPayload>({
			query: (body) => ({
				url: "media/jobs/phase-image",
				method: "POST",
				body,
				responseSchema: MediaJobEnvelopeSchema,
			}),
			transformResponse: (response: { job: MediaJob }) => response.job,
			invalidatesTags: ["MediaJobs", "MediaCosts"],
		}),
		getMediaJobs: builder.query<{ items: MediaJob[]; total: number }, MediaJobQuery | void>({
			query: (params) => ({
				url: "media/jobs",
				params: params
					? {
							...(params.courseId && { courseId: params.courseId }),
							...(params.status && { status: params.status }),
							...(params.page && { page: params.page }),
							...(params.limit && { limit: params.limit }),
						}
					: undefined,
				responseSchema: MediaJobListSchema,
			}),
			providesTags: ["MediaJobs"],
		}),
		getMediaJob: builder.query<MediaJob, number>({
			query: (id) => ({
				url: `media/jobs/${id}`,
				responseSchema: MediaJobSchema,
			}),
			providesTags: (_result, _error, id) => [{ type: "MediaJob" as const, id }],
		}),
		cancelMediaJob: builder.mutation<MediaJob, number>({
			query: (id) => ({
				url: `media/jobs/${id}/cancel`,
				method: "POST",
				responseSchema: MediaJobSchema,
			}),
			invalidatesTags: (_result, _error, id) => ["MediaJobs", { type: "MediaJob" as const, id }],
		}),
		/**
		 * Picking a variant is what creates the course-file. The response is identical to
		 * an upload, so the caller writes fullFilePath into phase content unchanged.
		 */
		selectMediaJobAsset: builder.mutation<
			CourseFileInfo,
			{ jobId: number; assetId: number; fileAssignment?: string }
		>({
			query: ({ jobId, ...body }) => ({
				url: `media/jobs/${jobId}/select-asset`,
				method: "POST",
				body,
				responseSchema: CourseFileInfoSchema,
			}),
			invalidatesTags: (_result, _error, { jobId }) => [
				"MediaJobs",
				{ type: "MediaJob" as const, id: jobId },
			],
		}),

		/**
		 * Always the caller's own company — the server scopes it off the ledger's own column
		 * and takes no company parameter, so there is nothing here to get wrong.
		 */
		getCompanyCosts: builder.query<CompanyCosts, CompanyCostsQuery | void>({
			query: (params) => ({
				url: "media/costs/company",
				params: params
					? { ...(params.from && { from: params.from }), ...(params.to && { to: params.to }) }
					: undefined,
				responseSchema: CompanyCostsSchema,
			}),
			providesTags: ["MediaCosts"],
		}),

		/**
		 * The media library. Without a `courseId` it is everything the company has generated,
		 * which is what makes reuse across scenarios possible.
		 */
		getMediaLibrary: builder.query<LibraryAsset[], MediaLibraryQuery | void>({
			query: (params) => ({
				url: "media/library",
				// Filter and order are the server's work; the client states its choices.
				params: Object.fromEntries(
					Object.entries(params ?? {}).filter(([, value]) => value !== undefined)
				),
				responseSchema: LibraryAssetSchema.array(),
			}),
			providesTags: ["MediaLibrary"],
		}),

		/**
		 * The company's folder tree with per-folder counts, and the scenarios its files come
		 * from. One request answers both questions the library page has before it shows a
		 * single file.
		 */
		getMediaFolders: builder.query<MediaFolderTree, void>({
			query: () => ({ url: "media/library/folders", responseSchema: MediaFolderTreeSchema }),
			providesTags: ["MediaFolders"],
		}),
		createMediaFolder: builder.mutation<MediaFolderSummary, { name: string; parentId?: number }>({
			query: (body) => ({
				url: "media/library/folders",
				method: "POST",
				body,
				responseSchema: MediaFolderSummarySchema,
			}),
			invalidatesTags: ["MediaFolders"],
		}),
		/** Rename, move, or both. `parentId` null moves the folder to the root. */
		updateMediaFolder: builder.mutation<
			MediaFolderSummary,
			{ id: number; name?: string; parentId?: number | null }
		>({
			query: ({ id, ...body }) => ({
				url: `media/library/folders/${id}`,
				method: "PATCH",
				body,
				responseSchema: MediaFolderSummarySchema,
			}),
			invalidatesTags: ["MediaFolders"],
		}),
		/** Refused by the server while the folder still holds anything; the message says what. */
		deleteMediaFolder: builder.mutation<void, number>({
			query: (id) => ({ url: `media/library/folders/${id}`, method: "DELETE" }),
			invalidatesTags: ["MediaFolders"],
		}),
		/**
		 * Moves one file into a folder — or, with `folderId` null, back out to Unfiled. The
		 * kind travels with the id because generated files and uploads live in different
		 * tables. Both the tree's counts and the grid change, so both are invalidated.
		 */
		moveLibraryItem: builder.mutation<
			void,
			{ folderId: number | null; kind: "generated" | "uploaded"; id: number }
		>({
			query: ({ folderId, kind, id }) => ({
				url: `media/library/folders/${folderId ?? 0}/items`,
				method: "POST",
				body: { kind, id },
			}),
			invalidatesTags: ["MediaFolders", "MediaLibrary"],
		}),

		/**
		 * One library file as bytes to save — a real download, where the bucket URL could
		 * only open a tab showing the file's first frame.
		 */
		downloadLibraryAsset: builder.mutation<
			{ blob: Blob; filename: string },
			{ kind: "generated" | "uploaded"; id: number }
		>({
			query: ({ kind, id }) => ({
				url: "media/library/download",
				params: { kind, id },
				responseHandler: (response) => response.blob(),
			}),
			transformResponse: (blob: Blob, meta) => {
				const response = (meta as { response?: Response } | undefined)?.response;
				const disposition = response?.headers.get("content-disposition") ?? "";
				const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "download";

				return { blob, filename };
			},
		}),

		assignLibraryAsset: builder.mutation<CourseFileInfo, AssignAssetPayload>({
			query: ({ assetId, kind, ...body }) => ({
				// Generated files and uploaded ones live in different tables, so the kind picks
				// the endpoint rather than the server having to guess from an id.
				url: `media/library/${kind === "uploaded" ? "uploads" : "assets"}/${assetId}/assign`,
				method: "POST",
				body,
				responseSchema: CourseFileInfoSchema,
			}),
			// The course's own files change too, so the scenario page stops showing the old
			// trailer the moment a new one is chosen.
			invalidatesTags: ["MediaLibrary", "Courses"],
		}),
		unassignLibraryAsset: builder.mutation<void, number>({
			query: (courseFileId) => ({
				url: `media/library/course-files/${courseFileId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["MediaLibrary", "Courses"],
		}),
		/** Puts a file into the library, from where any phase can point at it. */
		uploadToLibrary: builder.mutation<CourseFileInfo, UploadToLibraryPayload>({
			query: ({ file, courseId }) => {
				const body = new FormData();

				body.append("file", file);
				body.append("courseId", String(courseId));

				return {
					url: "media/library/upload",
					method: "POST",
					body,
					responseSchema: CourseFileInfoSchema,
				};
			},
			invalidatesTags: ["MediaLibrary"],
		}),
		deleteLibraryAsset: builder.mutation<void, number>({
			query: (assetId) => ({
				url: `media/library/assets/${assetId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["MediaLibrary"],
		}),

		getScenarioCosts: builder.query<ScenarioCosts, number>({
			query: (courseId) => ({
				url: `media/costs/scenario/${courseId}`,
				responseSchema: ScenarioCostsSchema,
			}),
			providesTags: ["MediaCosts"],
		}),

		/**
		 * The only source of a price in the product. The mockups compute
		 * `variants × formats × tierPrice` in JavaScript; duplicating that here would
		 * drift the moment an admin edits the registry.
		 */
		estimateMediaCost: builder.mutation<
			MediaEstimate,
			{
				useCase: AiModelUseCase;
				aiModelId?: number;
				qualityTier?: QualityTier;
				quantities: EstimateQuantities;
			}
		>({
			query: (body) => ({
				url: "media/estimate",
				method: "POST",
				body,
				responseSchema: EstimateSchema,
			}),
		}),
	}),
});

export const {
	useGetFormatPresetsQuery,
	useCreateFormatPresetMutation,
	useUpdateFormatPresetMutation,
	useDeleteFormatPresetMutation,
	useGetAiModelsQuery,
	useUpdateAiModelMutation,
	useGetMusicTracksQuery,
	useCreateMusicTrackMutation,
	useUpdateMusicTrackMutation,
	useDeleteMusicTrackMutation,
	useGenerateMusicTrackMutation,
	useEstimateMediaCostMutation,
	useCreatePhaseImageJobMutation,
	useGetMediaJobsQuery,
	useGetMediaJobQuery,
	useCancelMediaJobMutation,
	useSelectMediaJobAssetMutation,
	useGetScenarioCostsQuery,
	useGetCompanyCostsQuery,
	useGetMediaLibraryQuery,
	useGetMediaFoldersQuery,
	useCreateMediaFolderMutation,
	useUpdateMediaFolderMutation,
	useDeleteMediaFolderMutation,
	useMoveLibraryItemMutation,
	useDownloadLibraryAssetMutation,
	useAssignLibraryAssetMutation,
	useUnassignLibraryAssetMutation,
	useDeleteLibraryAssetMutation,
	useUploadToLibraryMutation,
} = mediaApi;
